'use client';

import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEventName } from '@/api/analytics';
import { ErrorServe } from '@/components/form/groups/ErrorGroup';
import { camelToNormalize } from '@/helpers/common';
import { showTooltip } from '@/helpers/tooltip';
import { useAnalytics } from '@/hooks/analytics';
import { useNavigation } from '@/hooks/navigation';
import { useNDBFlow } from '@/hooks/useNDBFlow';
import { useCommonStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { TooltipTypes } from '@/store/tooltipStore';

export const ConfirmEmail: FC<{ errorConfirmationEmail?: string | Error }> = ({
  errorConfirmationEmail,
}) => {
  const { navigate } = useNavigation();

  const { t } = useTranslation('common');
  const { sendEventToAnalytics } = useAnalytics();
  const fetchUser = useCommonStore.use.fetchUser();
  const { isNDBFlow, localNDBFlow } = useNDBFlow();

  const errors = useMemo(() => {
    if (!errorConfirmationEmail) return [];

    const keys = Object.keys(errorConfirmationEmail);

    return keys.map((key) => ({
      field: key,
      message: (errorConfirmationEmail as any)[key],
    }));
  }, [errorConfirmationEmail]);

  useEffect(() => {
    if (!errorConfirmationEmail) {
      fetchUser(REASON_FOR_UPDATING_USER.EMAIL_CONFIRMATION);

      sendEventToAnalytics({
        name: AnalyticsEventName.EMAIL_CONFIRMED,
      });

      showTooltip({
        type: TooltipTypes.SUCCESS,
        body: t('verifyEmailPopup.emailConfirmed'),
      });

      if (isNDBFlow && !localNDBFlow?.isNDBEmailVerifiedShown) {
        navigate('/?modal=ndb-email-verified', true);
      }
    }

    navigate('/', true);
  }, [
    errorConfirmationEmail,
    sendEventToAnalytics,
    fetchUser,
    navigate,
    t,
    isNDBFlow,
  ]);

  useEffect(() => {
    if (!errors.length) return;

    showTooltip({
      type: TooltipTypes.ERROR,
      body: <ErrorTooltip errors={errors} />,
    });
  }, [errors]);

  return null;
};

export const ErrorTooltip: FC<{ errors: ErrorServe[] }> = ({ errors }) => {
  return (
    <>
      {errors.map(({ field, message }, index) => (
        <span key={index} style={{ textTransform: 'capitalize' }}>
          <span>{camelToNormalize(field)}</span>:{' '}
          {typeof message === 'string' && message}
          {Array.isArray(message) && message.join(', ')}
        </span>
      ))}
    </>
  );
};
